
import { useState, useEffect, useMemo } from "react"
import { ChevronLeft, ChevronRight, CalendarIcon, LayoutList, HelpCircle, FilterIcon } from 'lucide-react'
import {
  format,
  addDays,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  parseISO,
  addWeeks,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from "date-fns"
import type { DateRange } from "react-day-picker"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import { MonthView } from "./month-view"
import { WeekView } from "./week-view"
import { DayView } from "./day-view"
import { GridView } from "./grid-view"

import { ViewToggle, type ViewMode } from "./view-toggle"

export type CalendarEvent = {
  id: string
  title: string
  start: Date | string
  end: Date | string
  color?: string
  tags?: Array<{
    label: string
    color: string
  }>
  user?: {
    name: string
    avatar?: string
  }
  description?: string
  location?: string
  is_exclusive?: boolean
  remaining_capacity?: number
  originalData?: any // For passing original booking data to modals
}

export type CalendarViewType = "month" | "week" | "day"

interface CalendarProps {
  events: CalendarEvent[]
  initialView?: CalendarViewType
  initialDate?: Date
  onCreateEvent?: () => void
  onViewEvent?: (event: CalendarEvent) => void
  onEditEvent?: (event: CalendarEvent) => void
  onDeleteEvent?: (event: CalendarEvent) => void
  onFilterEvents?: () => void
  isFilterApplied?: boolean
  isCustomView?: boolean
  onPrevious?: () => void
  onNext?: () => void
  disableNavigation?: boolean
  onDateRangeChange?: (startDate: Date, endDate: Date) => void
  onViewModeChange?: (viewMode: "calendar" | "grid") => void
  onCalendarViewChange?: (view: CalendarViewType) => void // New prop
  refetch?: () => void
  access?: string
  hideEventActions?: boolean
  // Grid view server-side pagination props
  gridViewProps?: {
    totalItems?: number
    currentPage?: number
    itemsPerPage?: number
    isLoading?: boolean
    onLimitChange?: (limit: number) => void
    onNextPage?: () => void
    onPrevPage?: () => void
    onFirstPage?: () => void
    onLastPage?: () => void
    isLastPage?: boolean
  }
}

export function Calendar({
  events,
  initialView = "month",
  initialDate = new Date(),
  onCreateEvent,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  onFilterEvents,
  isCustomView = false,
  onPrevious,
  onNext,
  disableNavigation = false,
  onDateRangeChange,
  onViewModeChange,
  onCalendarViewChange,
  refetch,
  access,
  gridViewProps,
  isFilterApplied,
  hideEventActions = false,
}: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(() => {
    const date = initialDate || new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  });
  const [calendarView, setCalendarView] = useState<CalendarViewType>(initialView)

  const [viewMode, setViewMode] = useState<ViewMode>("calendar")
  const [filterDateRange, setFilterDateRange] = useState<DateRange | undefined>(undefined)

  // Handle view mode changes
  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode)
    if (onViewModeChange) {
      onViewModeChange(newViewMode)
    }
  }

  // Update currentDate when initialDate prop changes
  useEffect(() => {
    console.log('Calendar: initialDate changed to:', initialDate);
    if (initialDate && initialDate instanceof Date) {
      const newDate = new Date(initialDate.getFullYear(), initialDate.getMonth(), initialDate.getDate());
      console.log('Calendar: setting currentDate to:', newDate);
      setCurrentDate(newDate);
    }
  }, [initialDate]); // Run when initialDate changes

  // Update calendarView when initialView prop changes
  useEffect(() => {
    console.log('Calendar: initialView changed to:', initialView);
    if (initialView && initialView !== calendarView) {
      console.log('Calendar: setting calendarView to:', initialView);
      setCalendarView(initialView);
    }
  }, [initialView]); // Run when initialView changes

  // Notify parent component when date range changes (for server-side data fetching)
  // Use a timeout to debounce rapid date changes
  useEffect(() => {
    if (onDateRangeChange && viewMode === "calendar") {
      const timeoutId = setTimeout(() => {
        const { start, end } = getVisibleDateRange();
        onDateRangeChange(start, end);
      }, 100); // Small debounce to prevent rapid fire calls

      return () => clearTimeout(timeoutId);
    }
  }, [currentDate, calendarView, viewMode]);

  const handlePrevious = () => {
    console.log('Calendar: handlePrevious called');
    if (onPrevious) {
      console.log('Calendar: calling onPrevious callback');
      onPrevious();
    } else {
      console.log('Calendar: using internal navigation');
      setCurrentDate((prev) => {
        try {
          let newDate: Date;
          if (calendarView === "month") {
            newDate = addMonths(prev, -1);
          } else if (calendarView === "week") {
            newDate = addWeeks(prev, -1);
          } else {
            newDate = addDays(prev, -1);
          }
          console.log('Calendar: internal navigation to:', newDate);
          return newDate;
        } catch (error) {
          console.error('Error navigating previous:', error);
          return prev; // Return previous state if error
        }
      });
    }
  }

  const handleNext = () => {
    console.log('Calendar: handleNext called');
    if (onNext) {
      console.log('Calendar: calling onNext callback');
      onNext();
    } else {
      console.log('Calendar: using internal navigation');
      setCurrentDate((prev) => {
        try {
          let newDate: Date;
          if (calendarView === "month") {
            newDate = addMonths(prev, 1);
          } else if (calendarView === "week") {
            newDate = addWeeks(prev, 1);
          } else {
            newDate = addDays(prev, 1);
          }
          console.log('Calendar: internal navigation to:', newDate);
          return newDate;
        } catch (error) {
          console.error('Error navigating next:', error);
          return prev; // Return previous state if error
        }
      });
    }
  }

  const getDateRangeText = () => {
    try {
      if (calendarView === "month" || calendarView === "week") {
        const start = calendarView === "month" ? startOfMonth(currentDate) : startOfWeek(currentDate, { weekStartsOn: 1 })
        const end = calendarView === "month" ? endOfMonth(currentDate) : endOfWeek(currentDate, { weekStartsOn: 1 })
        return `${format(start, "dd-MM-yyyy")} to ${format(end, "dd-MM-yyyy")}`
      } else {
        return format(currentDate, "dd-MM-yyyy")
      }
    } catch (error) {
      console.error('Error formatting date range:', error);
      return format(new Date(), "dd-MM-yyyy"); // Fallback to today's date
    }
  }

  // Get the visible date range based on current view
  const getVisibleDateRange = () => {
    if (calendarView === "month") {
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(currentDate)
      const viewStart = startOfWeek(monthStart, { weekStartsOn: 1 })
      const viewEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
      return { start: viewStart, end: viewEnd }
    } else if (calendarView === "week") {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 })
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 })
      return { start: weekStart, end: weekEnd }
    } else {
      // Day view
      return { start: currentDate, end: currentDate }
    }
  }

  const dateRange = getVisibleDateRange()

  // Normalize events to ensure start and end are Date objects and filter by date range if set
  const normalizedEvents = useMemo(() => {
    try {
      return events
        .map((event) => {
          try {
            return {
              ...event,
              start: event.start instanceof Date ? event.start : parseISO(event.start as string),
              end: event.end instanceof Date ? event.end : parseISO(event.end as string),
            };
          } catch (error) {
            console.warn('Error parsing event dates:', event, error);
            return {
              ...event,
              start: new Date(),
              end: new Date(),
            };
          }
        })
        .filter((event) => {
          // If no filter date range is set, don't filter events
          if (!filterDateRange?.from) return true;

          try {
            const eventStart = new Date(event.start);

            if (filterDateRange.to) {
              // If we have a complete range, check if event is within range
              return isWithinInterval(eventStart, {
                start: startOfDay(filterDateRange.from),
                end: endOfDay(filterDateRange.to),
              });
            } else {
              // If we only have a start date, check if event is on that day
              return isWithinInterval(eventStart, {
                start: startOfDay(filterDateRange.from),
                end: endOfDay(filterDateRange.from),
              });
            }
          } catch (error) {
            console.warn('Error filtering event:', event, error);
            return true; // Include event if filtering fails
          }
        });
    } catch (error) {
      console.error('Error normalizing events:', error);
      return []; // Return empty array if everything fails
    }
  }, [events, filterDateRange]);

  return (
    <div className="flex flex-col h-full border rounded  overflow-hidden bg-white">
      {/* header */}
      <div className={`flex items-center justify-between border-b ${!isCustomView ? "p-3 " : "p-0"}`}>
        <div className="flex items-center space-x-2">
          {!disableNavigation && viewMode === "calendar" && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevious}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{getDateRangeText()}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center space-x-2 ">
          {viewMode === "calendar" && (
            <Select
              value={calendarView}
              onValueChange={(value) => {
                const newView = value as CalendarViewType;
                setCalendarView(newView);
                if (onCalendarViewChange) { // Call new prop
                  onCalendarViewChange(newView);
                }
              }}
            >
              <SelectTrigger
                className={`h-8 w-[80px] text-xs
                ${!isCustomView ? "" : "hidden"}
                `}
              >
                <SelectValue placeholder="View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="day">Day</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* {isCustomView && onCreateEvent && (
            <Button
              onClick={onCreateEvent}
              className="h-8 bg-primary hover:bg-green-600 text-white text-xs"
              size="sm"
            >
              Create New
            </Button>
          )} */}
          {onCreateEvent && (
            <Button
              access={access !== "read"}
              onClick={onCreateEvent}
              className={`h-8 text-white text-xs ${!isCustomView ? "bg-primary hover:bg-green-600" : "hidden"
                }`}
              size="sm"
            >
              Create New
            </Button>
          )}

          {!isCustomView && <ViewToggle
            view={viewMode}
            onViewChange={handleViewModeChange}
            className="h-8"
          />}

          {!isCustomView && (
            <Button
              variant="outline"
              size="icon"
              className={`h-8 w-8 rounded-full border-[1px] border-gray-300  ${isFilterApplied ? "text-primary border-primary hover:text-primary hover:bg-transparent " : "text-gray-300 hover:text-gray-300 hover:bg-transparent "}`}
              onClick={onFilterEvents}
            >
              <FilterIcon className="h-4 w-4" />
            </Button>
          )}

          {/* <Button variant="outline" size="icon" className="h-8 w-8">
            <HelpCircle className="h-4 w-4" />
          </Button> */}
        </div>
      </div>
      {/* content */}
      <div className="flex-1 relative">
        {viewMode === "calendar" ? (
          <>
            {calendarView === "month" && (
              <MonthView
                currentDate={currentDate}
                events={normalizedEvents}
                dateRange={dateRange}
                onViewEvent={onViewEvent}
                onEditEvent={onEditEvent}
                onDeleteEvent={onDeleteEvent}
                refetch={refetch}
                access={access}
                hideEventActions={hideEventActions}
              />
            )}
            {calendarView === "week" && (
              <WeekView
                currentDate={currentDate}
                events={normalizedEvents}
                dateRange={dateRange}
                onViewEvent={onViewEvent}
                onEditEvent={onEditEvent}
                onDeleteEvent={onDeleteEvent}
                refetch={refetch}
                access={access}
                hideEventActions={hideEventActions}
              />
            )}
            {calendarView === "day" && (
              <DayView
                currentDate={currentDate}
                events={normalizedEvents}
                onViewEvent={onViewEvent}
                onEditEvent={onEditEvent}
                onDeleteEvent={onDeleteEvent}
                refetch={refetch}
                access={access}
                hideEventActions={hideEventActions}
              />
            )}
          </>
        ) : (
          <GridView
            events={normalizedEvents}
            onViewEvent={onViewEvent}
            onEditEvent={onEditEvent}
            onDeleteEvent={onDeleteEvent}
            refetch={refetch}
            access={access}
            hideEventActions={hideEventActions}
            totalItems={gridViewProps?.totalItems || 0}
            currentPage={gridViewProps?.currentPage || 0}
            itemsPerPage={gridViewProps?.itemsPerPage || 10}
            isLoading={gridViewProps?.isLoading || false}
            onLimitChange={gridViewProps?.onLimitChange || (() => { })}
            onNextPage={gridViewProps?.onNextPage || (() => { })}
            onPrevPage={gridViewProps?.onPrevPage || (() => { })}
            onFirstPage={gridViewProps?.onFirstPage || (() => { })}
            onLastPage={gridViewProps?.onLastPage || (() => { })}
            isLastPage={gridViewProps?.isLastPage || false}
          />
        )}
      </div>
    </div>
  );
}
